import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, DollarSign, TrendingUp, Home, ShoppingBag, Utensils, Car, Building2, LineChart, Target, DollarSign as Money } from 'lucide-react';
import { GoogleMap, useLoadScript, Marker, Circle, HeatmapLayer } from '@react-google-maps/api';

// Define interfaces for our data structures
interface UserPreferences {
  maxBudget: number;
  interests: string[];
  radius: number; // in kilometers
  categories: string[];
}

interface LocationData {
  id: string;
  name: string;
  position: { lat: number; lng: number };
  spending: number;
  category: string;
  timestamp: Date;
  isAffordable: boolean;
  domain?: string; // Added for logo support
}

interface Location {
  name: string;
  spending: number;
  transactions: number;
  trend: string;
  categories: string[];
  topSpots: {
    name: string;
    amount: number;
    domain?: string; // Added for logo support
  }[];
}

const libraries: ("places" | "geometry" | "drawing" | "visualization")[] = ["places"];

const LocationInsights = () => {
  // Replace with your actual Google Maps API key
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "YOUR_API_KEY_HERE",
    libraries
  });
  
  const [userLocation, setUserLocation] = useState({ lat: 40.7128, lng: -74.0060 });
  const [selectedLocation, setSelectedLocation] = useState('Downtown');
  const [userPreferences, setUserPreferences] = useState<UserPreferences>({
    maxBudget: 1000,
    interests: ['Dining', 'Entertainment', 'Shopping'],
    radius: 5,
    categories: ['Restaurant', 'Retail', 'Entertainment']
  });
  const [visitedLocations, setVisitedLocations] = useState<LocationData[]>([]);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);

  const locations: Location[] = [
    {
      name: "Downtown",
      spending: 2450.75,
      transactions: 15,
      trend: "+12%",
      categories: ["Dining", "Entertainment", "Shopping"],
      topSpots: [
        { name: "Starbucks", amount: 450, domain: "starbucks.com" },
        { name: "Walmart", amount: 850, domain: "walmart.com" },
        { name: "AMC Theatres", amount: 650, domain: "amctheatres.com" }
      ]
    },
    {
      name: "Midtown",
      spending: 1875.50,
      transactions: 8,
      trend: "+5%",
      categories: ["Groceries", "Fitness", "Coffee"],
      topSpots: [
        { name: "Planet Fitness", amount: 200, domain: "planetfitness.com" },
        { name: "Whole Foods", amount: 450, domain: "wholefoods.com" },
        { name: "Dunkin'", amount: 175, domain: "dunkindonuts.com" }
      ]
    },
    {
      name: "Uptown",
      spending: 950.25,
      transactions: 6,
      trend: "-3%",
      categories: ["Shopping", "Services"],
      topSpots: [
        { name: "Target", amount: 300, domain: "target.com" },
        { name: "Best Buy", amount: 450, domain: "bestbuy.com" },
        { name: "Nike Store", amount: 200, domain: "nike.com" }
      ]
    }
  ];

  const popularLocations = [
    { name: "Central Park", spent: 450, visits: 12, type: "Entertainment" },
    { name: "Whole Foods Market", spent: 850, visits: 8, type: "Groceries", domain: "wholefoods.com" },
    { name: "Planet Fitness", spent: 200, visits: 20, type: "Health", domain: "planetfitness.com" }
  ];

  const neighborhoodStats = [
    {
      name: "Upper East Side",
      avgSpending: 4200,
      popularity: 92,
      topCategories: ["Dining", "Shopping"],
      icon: <Building2 className="text-[#68A511]" />
    },
    {
      name: "Brooklyn Heights",
      avgSpending: 3800,
      popularity: 85,
      topCategories: ["Entertainment", "Fitness"],
      icon: <Building2 className="text-[#68A511]" />
    }
  ];

  // Map styling options
  const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '12px'
  };

  const mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    styles: [
      {
        featureType: "all",
        elementType: "labels.text.fill",
        stylers: [{ color: "#68A511" }]
      }
    ]
  };

  // Get user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  }, []);

  // Mock function to simulate getting visited locations from an API
  const fetchVisitedLocations = async () => {
    const mockLocations: LocationData[] = [
      {
        id: '1',
        name: 'Local Coffee Shop',
        position: { lat: 40.7128, lng: -74.0060 },
        spending: 25,
        category: 'Dining',
        timestamp: new Date(),
        isAffordable: true
      }
    ];
    setVisitedLocations(mockLocations);
  };

  useEffect(() => {
    fetchVisitedLocations();
  }, []);

  // Calculate if a location is within budget
  const isWithinBudget = (spending: number) => {
    return spending <= userPreferences.maxBudget;
  };

  // Generate heatmap data from visited locations
  const getHeatmapData = () => {
    return visitedLocations.map(location => ({
      location: new google.maps.LatLng(location.position.lat, location.position.lng),
      weight: location.spending
    }));
  };

  // Function to get logo URL
  const getLogoUrl = (domain: string) => {
    return `https://logo.clearbit.com/${domain}`;
  };

  // Function to handle logo loading errors
  const handleLogoError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const fallbackIcon = '/fallback-store-icon.png'; // Make sure to add this to your public folder
    e.currentTarget.src = fallbackIcon;
  };

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-[400px] bg-white/30 backdrop-blur-md rounded-xl mt-16">
        <div className="text-red-500 text-lg">Error loading map</div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-[400px] bg-white/30 backdrop-blur-md rounded-xl mt-16">
        <div className="text-[#68A511] text-lg">Loading map...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-16">
      {/* Map Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/30 backdrop-blur-md rounded-xl p-6 shadow-lg"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#DCE9C2]">
              <MapPin className="text-[#68A511]" size={24} />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Location Insights</h1>
          </div>
          <button
            onClick={() => setShowPreferencesModal(true)}
            className="px-4 py-2 bg-[#DCE9C2] text-[#68A511] rounded-lg hover:bg-[#68A511] hover:text-white transition-all duration-300"
          >
            Update Preferences
          </button>
        </div>

        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={userLocation}
          zoom={13}
          options={mapOptions}
        >
          {/* User's location marker */}
          <Marker
            position={userLocation}
          />

          {/* Radius circle */}
          <Circle
            center={userLocation}
            radius={userPreferences.radius * 1000}
            options={{
              fillColor: '#68A511',
              fillOpacity: 0.1,
              strokeColor: '#68A511',
              strokeOpacity: 0.8,
              strokeWeight: 2,
            }}
          />

          {/* Visited locations markers */}
          {visitedLocations.map((location) => (
            <Marker
              key={location.id}
              position={location.position}
              onClick={() => setSelectedLocation(location.name)}
            />
          ))}
        </GoogleMap>
      </motion.div>

      {/* Location Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/30 backdrop-blur-md rounded-xl p-6 shadow-lg"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-[#DCE9C2]">
            <MapPin className="text-[#68A511]" size={24} />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Location Insights</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((location, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setSelectedLocation(location.name)}
              className={`bg-white/50 backdrop-blur-sm rounded-xl p-6 shadow-md hover:shadow-lg transition-all cursor-pointer
                ${selectedLocation === location.name ? 'ring-2 ring-[#68A511]' : ''}`}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{location.name}</h3>
                <span className={`px-2 py-1 rounded-lg text-sm ${
                  location.trend.startsWith('+') 
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {location.trend}
                </span>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="text-[#68A511]" size={16} />
                  <span className="text-gray-600">Total Spending:</span>
                  <span className="font-semibold">${location.spending.toLocaleString()}</span>
                </div>

                <div className="flex items-center gap-2">
                  <LineChart className="text-[#68A511]" size={16} />
                  <span className="text-gray-600">Transactions:</span>
                  <span className="font-semibold">{location.transactions}</span>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2">Top Categories:</p>
                  <div className="flex flex-wrap gap-2">
                    {location.categories.map((category, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-[#DCE9C2] text-[#68A511] rounded-full text-sm"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Top Spending Spots</p>
                  <div className="space-y-2">
                    {location.topSpots.map((spot, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {spot.domain && (
                            <img
                              src={getLogoUrl(spot.domain)}
                              alt={spot.name}
                              className="w-6 h-6 rounded-full object-contain"
                              onError={handleLogoError}
                            />
                          )}
                          <span className="text-sm text-gray-600">{spot.name}</span>
                        </div>
                        <span className="text-sm font-medium text-[#68A511]">${spot.amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Popular Locations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/30 backdrop-blur-md rounded-xl p-6 shadow-lg"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4">Popular Locations</h2>
          <div className="space-y-4">
            {popularLocations.map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 bg-[#DCE9C2]/50 rounded-xl hover:bg-[#DCE9C2]/70 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    {location.domain && (
                      <img
                        src={getLogoUrl(location.domain)}
                        alt={location.name}
                        className="w-6 h-6 rounded-full object-contain"
                        onError={handleLogoError}
                      />
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-800">{location.name}</h3>
                      <span className="text-sm text-gray-600">{location.type}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#68A511]">${location.spent}</p>
                    <span className="text-sm text-gray-600">{location.visits} visits</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Neighborhood Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/30 backdrop-blur-md rounded-xl p-6 shadow-lg"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4">Neighborhood Statistics</h2>
          <div className="space-y-4">
            {neighborhoodStats.map((neighborhood, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 bg-[#DCE9C2]/50 rounded-xl hover:bg-[#DCE9C2]/70 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  {neighborhood.icon}
                  <h3 className="font-semibold text-gray-800">{neighborhood.name}</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <p className="text-sm text-gray-600">Avg. Spending</p>
                    <p className="font-semibold text-[#68A511]">${neighborhood.avgSpending}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Popularity</p>
                    <p className="font-semibold text-[#68A511]">{neighborhood.popularity}%</p>
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-sm text-gray-600">Top Categories</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {neighborhood.topCategories.map((category, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-white/50 text-[#68A511] rounded-full text-xs"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Spending Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white/30 backdrop-blur-md rounded-xl p-6 shadow-lg"
      >
        <h2 className="text-xl font-bold text-gray-800 mb-4">Spending Insights</h2>
        <div className="space-y-4">
          <div className="p-4 bg-[#DCE9C2]/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="text-[#68A511]" size={20} />
              <span className="font-semibold text-gray-800">Spending Pattern Analysis</span>
            </div>
            <p className="text-gray-600">Your highest spending area is {selectedLocation}, primarily in dining and entertainment categories. Consider exploring more affordable options in nearby neighborhoods for similar activities.</p>
          </div>
        </div>
      </motion.div>

      {/* Preferences Modal */}
      {showPreferencesModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full mx-4"
          >
            <h2 className="text-xl font-bold text-gray-800 mb-4">Update Preferences</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Maximum Budget</label>
                <input
                  type="number"
                  value={userPreferences.maxBudget}
                  onChange={(e) => setUserPreferences({
                    ...userPreferences,
                    maxBudget: Number(e.target.value)
                  })}
                  className="w-full mt-1 p-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#68A511]"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Search Radius (km)</label>
                <input
                  type="number"
                  value={userPreferences.radius}
                  onChange={(e) => setUserPreferences({
                    ...userPreferences,
                    radius: Number(e.target.value)
                  })}
                  className="w-full mt-1 p-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#68A511]"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Interests</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['Dining', 'Entertainment', 'Shopping', 'Sports', 'Culture'].map((interest) => (
                    <button
                      key={interest}
                      onClick={() => {
                        const newInterests = userPreferences.interests.includes(interest)
                          ? userPreferences.interests.filter(i => i !== interest)
                          : [...userPreferences.interests, interest];
                        setUserPreferences({ ...userPreferences, interests: newInterests });
                      }}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        userPreferences.interests.includes(interest)
                          ? 'bg-[#68A511] text-white'
                          : 'bg-[#DCE9C2] text-[#68A511]'
                      }`}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setShowPreferencesModal(false)}
                className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Save preferences
                  setShowPreferencesModal(false);
                }}
                className="px-4 py-2 bg-[#68A511] text-white rounded-lg hover:bg-[#527f0e] transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default LocationInsights; 